import firebase from 'firebase/compat/app';
import 'firebase/compat/analytics';
import posthog from 'posthog-js';

import {
  AnalyticsProperties,
  AnalyticsService,
  AnalyticsUserProperties,
} from '@credistream/shared';
import { loadMetaPixel, disableMetaPixel } from '~/components/MetaPixel/MetaPixel';

type Dependecies = {
  postHogApiKey: string;
};

export class NoOpAnalyticsService implements AnalyticsService {
  track(_event_name: string, _properties?: AnalyticsProperties): void {}
  identify(_userId: string, _userProperties?: AnalyticsUserProperties): void {}
  updateUserProperties(_userId: string, _userProperties: AnalyticsUserProperties): void {}
}

export class WebAnalyticsService implements AnalyticsService {
  firebase: firebase.analytics.Analytics;
  posthog: typeof posthog;
  isLocalhost: boolean = window.location.hostname === 'localhost';

  constructor(dependencies: Dependecies) {
    if (!this.isLocalhost) {
      posthog.init(dependencies.postHogApiKey, {
        api_host: 'https://eu.posthog.com',
        autocapture: !this.isLocalhost,
        capture_pageleave: !this.isLocalhost,
        capture_pageview: !this.isLocalhost,
        capture_performance: !this.isLocalhost,
      });
    }

    this.firebase = firebase.analytics();
    this.posthog = posthog;
  }

  track(event_name: string, properties?: AnalyticsProperties): void {
    this.firebase.logEvent(event_name, properties);
    if (!this.isLocalhost) {
      this.posthog.capture(event_name, properties);
    }
  }

  identify(userId: string, userProperties?: AnalyticsUserProperties) {
    if (!this.isLocalhost) {
      this.firebase.setUserId(userId);
      this.posthog.identify(userId, {
        email: userProperties?.email || null,
        name: userProperties?.name || null,
      });
    }
  }

  updateUserProperties(userId: string, userProperties: AnalyticsUserProperties): void {
    if (!this.isLocalhost) {
      this.firebase.setUserProperties({ userId, email: userProperties.email || null });
      posthog.capture('update_user_properties', {
        $set: { email: userProperties.email || null },
      });
    }
  }

  teardown(): void {
    // Stop PostHog collection and clear stored identity/persistence
    this.posthog.opt_out_capturing();
    this.posthog.reset();
    // Stop Firebase Analytics collection
    this.firebase.setAnalyticsCollectionEnabled(false);
  }
}

export const COOKIE_CONSENT_KEY = 'credistream_cookie_consent';
export type CookieConsent = 'accepted' | 'rejected';

/**
 * Delegate-based analytics service. Starts as NoOp and switches to the real
 * service only after the user explicitly accepts analytics cookies.
 * The store is initialized with this instance once; calling enable()/disable()
 * mutates the delegate in-place so all Redux thunks pick up the change without
 * recreating the store.
 */
export class ConsentAwareAnalyticsService implements AnalyticsService {
  private delegate: AnalyticsService = new NoOpAnalyticsService();
  private readonly analyticsEnabled: boolean;
  private readonly postHogApiKey: string;

  constructor(dependencies: Dependecies & { analyticsEnabled: boolean }) {
    this.analyticsEnabled = dependencies.analyticsEnabled;
    this.postHogApiKey = dependencies.postHogApiKey;

    const stored = localStorage.getItem(COOKIE_CONSENT_KEY) as CookieConsent | null;
    if (stored === 'accepted') {
      this.initRealService();
    }
  }

  enable(): void {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    this.initRealService();
  }

  disable(): void {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    if (this.delegate instanceof WebAnalyticsService) {
      this.delegate.teardown();
    }
    disableMetaPixel();
    this.delegate = new NoOpAnalyticsService();
  }

  track(event_name: string, properties?: AnalyticsProperties): void {
    this.delegate.track(event_name, properties);
  }

  identify(userId: string, userProperties?: AnalyticsUserProperties): void {
    this.delegate.identify(userId, userProperties);
  }

  updateUserProperties(userId: string, userProperties: AnalyticsUserProperties): void {
    this.delegate.updateUserProperties(userId, userProperties);
  }

  private initRealService(): void {
    if (!this.analyticsEnabled) return;
    this.delegate = new WebAnalyticsService({ postHogApiKey: this.postHogApiKey });
    loadMetaPixel();
  }
}
